import { useEffect, useRef, useState } from 'react';
import { remainingLabel } from './presence-beat';
import { useFocusOnMount } from './useFocusOnMount';

// =====================================================================
// "Still there?"
//
// In the page flow and sticky, not floating over it. It has to be visible during a
// live call — the case it most exists for — and the call card carries hang-up controls
// at both its top and its bottom, so any floating panel covers one of them on exactly
// the calls where somebody needs to press it. A banner that displaces content can
// cover nothing.
// =====================================================================

interface Props {
  trigger: 'idle' | 'cap';
  expiresAt: string;
  // The answer travels over the socket, so with it down the button cannot do its job
  // and has to say so rather than swallow the press.
  online: boolean;
  onConfirm: () => void;
}

export function PresenceOverlay({
  trigger,
  expiresAt,
  online,
  onConfirm,
}: Props): React.ReactElement {
  const [now, setNow] = useState(() => Date.now());
  const confirmRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useFocusOnMount(confirmRef, rootRef);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const body =
    trigger === 'cap'
      ? "You've held this pool for a while — confirm you're still working it."
      : "We haven't seen any activity in this tab for a few minutes.";

  return (
    <div ref={rootRef} className="sticky top-0 z-50 -mx-6 mb-4 px-6 pt-2">
      <div
        role="alertdialog"
        aria-modal="false"
        aria-labelledby="presence-title"
        aria-describedby="presence-body"
        className="rounded-lg border-2 border-amber-500 bg-amber-50 p-4 shadow-lg"
      >
        <h2 id="presence-title" className="text-lg font-semibold text-amber-950">
          Still there?
        </h2>
        <p id="presence-body" className="mt-1 text-sm text-amber-950">
          {body}
        </p>
        <p className="mt-1 text-sm text-amber-950">
          You&rsquo;ll be taken out of the pool in{' '}
          <span className="font-mono font-semibold" aria-live="off">
            {remainingLabel(expiresAt, now)}
          </span>
          .
        </p>
        <button
          ref={confirmRef}
          type="button"
          onClick={onConfirm}
          disabled={!online}
          className="mt-3 w-full rounded-md bg-amber-700 px-4 py-2.5 text-base font-semibold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {online ? 'I’m here' : 'Reconnecting…'}
        </button>
      </div>
    </div>
  );
}
